<template>
  <AgentLayout>
    <div class="w-11/12 max-w-3xl mx-auto">
      <img v-if="team.logo" class="w-24 mb-12 mx-auto" :src="team.logo" />
      <h1 class="font-bold text-3xl text-center mb-4">Welcome to {{ team.company }}</h1>

      <ZeroState v-if="loaded && userTasks.length === 0" class="mt-12">
        <p class="font-bold text-gray-800">No tasks yet</p>
        <p class="mt-2 text-gray-600">Your team hasn't assigned anything to you &mdash; check back soon.</p>
      </ZeroState>

      <div v-else>
        <p class="text-center text-gray-600 max-w-xl mx-auto">
          We're super excited to have you. Please complete the following items as soon as possible so we can get you up
          and running!
        </p>
        <header v-if="incompleteTasks.length > 0" class="mt-12 text-sm text-gray-600 uppercase tracking-wider">
          Up Next
        </header>
        <div class="grid grid-cols-1 gap-1 mx-auto mt-2">
          <UserTaskPreviewCard
            v-for="(task, index) in incompleteTasks"
            :key="`tasks.${task.id}`"
            :task="task"
            :show-button="index == 0"
          />
        </div>
        <header v-if="completedTasks.length > 0" class="mt-12 text-sm text-gray-600 uppercase tracking-wider">
          Already Completed
        </header>
        <div class="grid grid-cols-1 gap-1 mx-auto mt-2">
          <UserTaskPreviewCard
            v-for="task in completedTasks"
            :key="`tasks.${task.id}`"
            :task="task"
            :show-button="false"
          />
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<script setup lang="ts">
import { Api } from '@/api';
import type { UserTaskEntity } from '@/api/entity_types/UserTask';
import AgentLayout from '@/components/layouts/AgentLayout/index.vue';
import UserTaskPreviewCard from '@/components/user_tasks/UserTaskPreviewCard.vue';
import ZeroState from '@/components/ZeroState.vue';
import { useTeamStore } from '@/stores/team';
import { confetti } from '@tsparticles/confetti';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

const teamStore = useTeamStore();
const { team } = storeToRefs(teamStore);
const userTasks = ref<UserTaskEntity[]>([]);
const loaded = ref(false);

const incompleteTasks = computed(() => userTasks.value.filter(task => task.status !== 'completed'));
const completedTasks = computed(() => userTasks.value.filter(task => task.status === 'completed'));
const allTasksCompleted = computed(
  () => userTasks.value.length > 0 && userTasks.value.every(task => task.status === 'completed')
);

onMounted(async () => {
  const response = await Api.UserTasks.listUserTasks();
  userTasks.value = response.records;
  loaded.value = true;

  if (allTasksCompleted.value) {
    setTimeout(async () => {
      const container = await confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      setTimeout(() => container?.destroy(), 5000);
    }, 1000);
  }
});
</script>
